import React from 'react';
import { Card, Tag, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { OrderHistoryCardProps } from '../../interfaces';
import OrderHistoryProductCard from '../OrderHistoryProductCard';
import fallbackImage from '../../../assets/img/ImgFV.jpg';
import { getOrderItemKey, getShortOrderId } from '../orderHistory.utils';

import './OrderHistoryCard.scss';

const { Text } = Typography;

const OrderHistoryCard: React.FC<OrderHistoryCardProps> = ({
  order,
  isCopied,
  onCopyOrderId,
  imageAndPriceByKey,
  imageStatusByKey,
  imageErrorByKey,
}) => {
  const shipping = order.shippingAddress;
  const recipient = shipping?.name || [shipping?.firstName, shipping?.lastName].filter(Boolean).join(' ');
  const addressLines = shipping
    ? [
      recipient,
      [shipping.addressLine1, shipping.addressLine2].filter(Boolean).join(', '),
      [shipping.city, shipping.province, shipping.postcode].filter(Boolean).join(', '),
      shipping.country,
    ].filter(Boolean)
    : [];

  return (
    <Card
      className="order-card"
      title={
        <div className="order-card-title">
          <span>Order: ID</span>
          <span className="order-card-id"> {getShortOrderId(order.id)}</span>
          <button
            type="button"
            className={`order-card-copy-button ${isCopied ? 'is-copied' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              onCopyOrderId(order.id);
            }}
            aria-label={isCopied ? 'Order ID copied' : 'Copy order ID'}
          >
            <CopyOutlined />
            <span>{isCopied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      }
      extra={<Text strong>Total: ${order.totalAmount}</Text>}
    >
      <div className="order-card-meta">
        <Tag color={order.status === 'PAID' ? 'green' : 'gold'}>{order.status || 'PAID'}</Tag>
        {order.paymentDate && <Text type="secondary">{new Date(order.paymentDate).toLocaleDateString()}</Text>}
      </div>
      <div className={`order-card-content ${shipping ? '' : 'order-card-content--products-only'}`}>
        <div className="product-list" aria-label="Purchased products">
          {order.items.map((item) => {
            const itemKey = getOrderItemKey(item.product.id, item.material);
            const materialData = imageAndPriceByKey[itemKey];
            const imageStatus = imageStatusByKey[itemKey];
            const isError = imageStatus === 'failed';
            const imageUrl = isError
              ? fallbackImage
              : materialData?.productImages?.[0]?.image?.publicUrl ?? fallbackImage;

            return (
              <OrderHistoryProductCard
                key={item.id}
                item={item}
                imageUrl={imageUrl}
                price={materialData?.price ?? null}
                isLoading={imageStatus === 'loading'}
                errorMessage={isError ? imageErrorByKey[itemKey] : null}
              />
            );
          })}
        </div>
        {shipping && (
          <div className="order-card-address">
            <Text strong>Delivery address</Text>
            <div className="order-card-address-lines">
              {addressLines.map((line) => (
                <Text key={line}>{line}</Text>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default React.memo(OrderHistoryCard);
